/* eslint-disable no-unused-vars */
import BleService from "../ble/BleService";
import helpers from "../../utils/helpers";
import FwuDataClient from './fwuDataClient';
import deviceService from "../device/deviceService";
import BLE_UUID_TYPES from "../../consts/bleUuidTypes";
import converter from "../../utils/converter";
import ConnectedExponentialBackOff from "../ble/connectedExponentialBackOff";

const FWU_STATUS_VALUES = {
    NO_IMAGE: '00',
    UPLOAD_PROCESSING: '01',
    UPGRADE_PROCESSING: '02',
    IMAGE_VERIFICATION_PROCESSING: '03',
    IMAGE_UPLOAD_ERROR: '80',
    IMAGE_STORAGE_ERROR: '81',
    IMAGE_SIGNATURE_ERROR: '82',
    PROCEDURE_NOT_ALLOWED: '83',
};

export default class DeviceFwuCharacteristicClient {
    constructor(options) {
        this.options = options;
        this.iqosBLEClient = undefined;
        this.isStopped = true;
        this.isLastFrameLoaded = false;
        this.isReadingFWUDataCharacteristic = false;
        this.exponentialBackOff = undefined;

        this.start();
    }

    start = async () => {
        try {
            const bleService = new BleService()
            this.iqosBLEClient = bleService.getDevice(this.options.deviceId);
            await this.iqosBLEClient.getFwuServices();
            await helpers.timeout(100);
            this.startFwUpdate();
        } catch (e) {
            console.info(`DeviceFwuCharacteristicClient: start error: ${e}`);
            this.onFwuFail();
        }
    };

    stop = async () => {
        if (this.isStopped) return;

        this.isReadingFWUDataCharacteristic = false;
        this.isStopped = true;
        this.stopFwuDataClient();

        deviceService.unsubscribeOnCharacteristic(this.options.deviceId, BLE_UUID_TYPES.FW_UPGRADE_STATUS, this.onFwuStatusNotification);
    };

    initNewFwuDataClient = () => {
        this.stopFwuDataClient();

        this.fwuDataClient = new FwuDataClient(
            {
                program_blocks: this.options.fwuPackageData.program_blocks,
                onLastFrame: this.onLastFrame,
                onFail: this.onFwuFail,
                sendStatusMessage: this.options.sendStatusMessage,
                deviceId: this.options.deviceId,
            }
        );
    };

    onLastFrame = () => {
        this.isLastFrameLoaded = true;
    };

    startFwUpdate = async () => {
        this.isStopped = false;

        console.debug(`DeviceFwuCharacteristicClient: start firmware update`);
        await deviceService.subscribeOnCharacteristic(this.options.deviceId, BLE_UUID_TYPES.FW_UPGRADE_STATUS, this.onFwuStatusNotification);
        await helpers.timeout(1000);

        this.sendStartUpgradeRequest();
    };

    sendStartUpgradeRequest = () => {
        this.tryToWriteValueToFwuControl(this.options.fwuPackageData.start_programming);
    };

    tryToWriteValueToFwuControl = async (value) => {
        const clearExponentialBackOff = () => (this.exponentialBackOff = undefined);
        const toTry = async () => {
            console.debug(`DeviceFwuCharacteristicClient: try to writeValue: ${value}`);
            await this.iqosBLEClient.writeValueToFwuControlCharacteristic(value);
        };
        const success = async () => {
            console.debug(`DeviceFwuCharacteristicClient: writeValue: ${value} success`);
            clearExponentialBackOff();
        };
        const fail = () => {
            console.debug(`DeviceFwuCharacteristicClient: writeValue: ${value} failed`);
            this.onFwuFail();
            clearExponentialBackOff();
        };

        this.exponentialBackOff = new ConnectedExponentialBackOff(this.options.deviceId);

        await new Promise((resolve, reject) => {
            this.exponentialBackOff.run(
            5,
            500,
            async () => await toTry(),
            success,
            fail
        );
        });
    };

    startFwuDataClient = (frameIndex = 0) => {
        if (this.isStopped) return;
        this.initNewFwuDataClient();

        console.log('############### START FWU CLIENT', frameIndex);

        this.fwuDataClient.start(frameIndex);
    };
    
    onFwuStatusNotification = async (value) => {
        if (this.isStopped) return;

        this.disableStartExponentialBackOff();
        this.stopFwuDataClient();

        console.debug(`DeviceFwuCharacteristicClient: fwu status message: ${JSON.stringify(value)} `);

        const response = value?.data?.data;

        switch (response.toString()) {
            case FWU_STATUS_VALUES.UPLOAD_PROCESSING:
                if (this.isReadingFWUDataCharacteristic) return;
                this.startFwuDataClient();
                return;
            case FWU_STATUS_VALUES.IMAGE_UPLOAD_ERROR:
                if (this.isLastFrameLoaded) {
                    this.onFwuFail(response);
                } else {
                    await helpers.timeout(50);
                    this.readFwuDataCharacteristic();
                }
                break;
            case FWU_STATUS_VALUES.NO_IMAGE:
            case FWU_STATUS_VALUES.PROCEDURE_NOT_ALLOWED:
                this.stop();

                if (!this.isLastFrameLoaded && this.options.canRetry) {
                    this.options.retryToUpdateFW();
                } else {
                    this.onFwuFail(response);
                }

                break;
            case FWU_STATUS_VALUES.IMAGE_STORAGE_ERROR:
            case FWU_STATUS_VALUES.IMAGE_SIGNATURE_ERROR:
                this.onFwuFail(response);
                break;
            case FWU_STATUS_VALUES.UPGRADE_PROCESSING:
                this.options.onUpgradeProcessing();
                return;
            default:
                break;
        }
    };

    setCharacteristicDataPolling = () => {
        this.iqosBLEClient.setCharacteristicDataPolling((value) => console.log("========== || setCharacteristicDataPolling: ", value));
    }

    readFwuDataCharacteristic = async () => {
        if (this.isReadingFWUDataCharacteristic) return;

        this.isReadingFWUDataCharacteristic = true;

        const toTry = async () => {
            console.debug(`DeviceFwuCharacteristicClient: try to readFwuDataCharacteristic`);
            this.iqosBLEClient.readFwuDataCharacteristic(this.onFwuDataCharacteristicMessage);
        };
        const success = async () => {
            console.debug(`DeviceFwuCharacteristicClient: readFwuDataCharacteristic success`);
        };
        const fail = () => {
            console.debug(`DeviceFwuCharacteristicClient: readFwuDataCharacteristic failed`);
            this.onFwuFail();
        };

        this.exponentialBackOff = new ConnectedExponentialBackOff(this.options.deviceId);

        await new Promise((resolve, reject) => {
            this.exponentialBackOff.run(
            3,
            100,
            async () => await toTry(),
            success,
            fail
        );
        });

    };

    onFwuDataCharacteristicMessage = async (value) => {
        if (this.isStopped) return;

        this.isReadingFWUDataCharacteristic = false;

        console.log("onFwuDataCharacteristicMessage : ", value);

        try {
            const convertHexIndexToInt = (hex) => {
                const hexIndexReversed = hex
                    .match(/.{1,2}/g)
                    .reverse()
                    .join('');
                const index = converter.hex2dec(hexIndexReversed);
            
                return parseInt(index);
            };

            const frameIndex = convertHexIndexToInt(value.characteristicData);
            const frame = this.options.fwuPackageData.program_blocks[frameIndex];
    
            console.debug(`DeviceFwuCharacteristicClient: read data characteristic: ${value?.characteristic}, frameIndex: ${frameIndex}`);
    
            if (frame) {
                console.debug(`DeviceFwuCharacteristicClient: try to resend frame: ${frameIndex} - ${frame?.f}`);
    
                await helpers.timeout(500);
                this.isReadingFWUDataCharacteristic = false;

                console.warn("############### RESEND FRAME", frameIndex, frame);
                this.startFwuDataClient(frameIndex);
            } else {
                this.isReadingFWUDataCharacteristic = false;
                this.onFwuFail(value);
            }
        } catch (e) {
            console.error(`DeviceFwuCharacteristicClient: onFwuDataCharacteristicMessage error: ${e}`);
            this.onFwuFail(value);
        }

        
    };

    stopFwuDataClient = () => {
        if (this.fwuDataClient) {
            this.fwuDataClient.stop();
        }
    };

    onFwuFail = (error) => {
        this.stop();

        console.error('DeviceFwuCharacteristicClient: onFwuFail', error || 'Firmware update error');

        this.options.onError(JSON.stringify(error || 'Firmware update error'));
    };

    disableStartExponentialBackOff = () => {
        if (this.exponentialBackOff) this.exponentialBackOff.disable();
    };

}